import type CreativeType from '@/Enums/Creatives/CreativeType.enum';
import type ItemStatus from '@/Enums/ItemStatus.enum';
import type { ILineItemCreative } from '@/Interfaces/CampaignManagement/LineItem.interface';
import CreativeTypeService from '@/Services/Creative/CreativeType.service';
import { TextField } from '@mui/material';
import { type ChangeEvent, useCallback, useMemo } from 'react';
import { type FieldErrors } from 'react-hook-form';

interface ClickUrlCellProps {
  id: number;
  type: CreativeType;
  value: string;
  errors: FieldErrors[];
  creativeIds: number[];
  onChange: (
    id: number,
    field: keyof Pick<ILineItemCreative, 'associationStatus' | 'startDate' | 'endDate' | 'clickUrl' | 'disassociated'>,
  ) => (value: ItemStatus | string | null | boolean) => void;
  disabled?: boolean;
}

const ClickUrlCell = ({ id, type, value, errors, creativeIds, onChange, disabled }: ClickUrlCellProps) => {
  if (!CreativeTypeService.hasClickUrl(type)) {
    return <>-</>;
  }

  const index = creativeIds.indexOf(id);
  const rawError = errors?.[index]?.clickUrl;
  const message = typeof rawError === 'object' && typeof rawError.message === 'string' ? rawError.message : null;

  const handler = useMemo(() => onChange(id, 'clickUrl'), [onChange, id]);
  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      handler(e.target.value || '');
    },
    [handler],
  );

  return (
    <TextField
      fullWidth
      variant='outlined'
      size='small'
      value={value}
      onChange={handleChange}
      error={!!message}
      helperText={message}
      disabled={disabled}
      slotProps={{
        input: {
          style: { fontWeight: 600 },
          sx: { height: '40px' },
        },
      }}
    />
  );
};

export default ClickUrlCell;
